<script setup lang="ts">
// Live-aware game detail: line scores, box score (team totals + player tables),
// and play-by-play. Every section degrades to nothing when the provider has no
// data for this game/sport.
interface Detail {
  provider_event_id?: string
  linescores?: { home: number[]; away: number[]; home_total: number | null; away_total: number | null
                 period_label?: string; state?: string; completed?: boolean }
  boxscore?: { teams?: { team: string; stats: { label: string; value: string }[] }[]
               players?: { team: string; label?: string; headers: string[]; rows: { provider_player_id: string; name: string; stats: string[]; dnp: boolean }[] }[] }
  plays?: { text: string; clock: string; period: number | string; period_label?: string; team: string; scoring: boolean }[]
}
const props = defineProps<{ detail: Detail | null; home: string; away: string }>()
const ls = computed(() => props.detail?.linescores || null)
const periods = computed(() => {
  const a = ls.value?.away?.length || 0
  const h = ls.value?.home?.length || 0
  return Math.max(a, h)
})
const periodLabel = computed(() => ls.value?.period_label || '')
const hasBox = computed(() => (props.detail?.boxscore?.teams?.length || 0) > 0 ||
  (props.detail?.boxscore?.players?.length || 0) > 0)
const plays = computed(() => props.detail?.plays || [])
// group consecutive plays by their period label so each inning/quarter/period
// gets a section header (#6). The list is newest-first; grouping preserves that.
const playGroups = computed(() => {
  const groups: { label: string; plays: typeof plays.value }[] = []
  for (const p of plays.value) {
    const label = p.period_label || ''
    const last = groups[groups.length - 1]
    if (last && last.label === label) last.plays.push(p)
    else groups.push({ label, plays: [p] })
  }
  return groups
})
</script>

<template>
  <div class="detail">
    <div v-if="ls && periods" class="card linescore">
      <table>
        <thead>
          <tr><th class="t">{{ periodLabel }}</th><th v-for="n in periods" :key="n" class="p">{{ n }}</th><th class="tot">T</th></tr>
        </thead>
        <tbody>
          <tr>
            <td class="t">{{ away }}</td>
            <td v-for="(v, i) in ls.away" :key="'a'+i" class="p mono">{{ v }}</td>
            <td class="tot mono">{{ ls.away_total ?? '—' }}</td>
          </tr>
          <tr>
            <td class="t">{{ home }}</td>
            <td v-for="(v, i) in ls.home" :key="'h'+i" class="p mono">{{ v }}</td>
            <td class="tot mono">{{ ls.home_total ?? '—' }}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <div v-if="hasBox" class="box">
      <div v-for="(t, ti) in detail!.boxscore!.teams || []" :key="'tt'+ti" class="card team-tot">
        <h4>{{ t.team }}</h4>
        <div class="tot-grid">
          <div v-for="s in t.stats" :key="s.label" class="tot-cell">
            <span class="tl">{{ s.label }}</span><span class="tv mono">{{ s.value }}</span>
          </div>
        </div>
      </div>
      <div v-for="(pt, pi) in detail!.boxscore!.players || []" :key="'pt'+pi" class="card ptable">
        <h4>{{ pt.team }}<span v-if="pt.label" class="sub"> · {{ pt.label }}</span></h4>
        <div class="scroll">
          <table>
            <thead><tr><th class="nm">Player</th><th v-for="h in pt.headers" :key="h">{{ h }}</th></tr></thead>
            <tbody>
              <tr v-for="r in pt.rows" :key="r.provider_player_id || r.name">
                <td class="nm">{{ r.name }}</td>
                <td v-for="(s, si) in r.stats" :key="si" class="mono">{{ s }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div v-if="plays.length" class="card plays">
      <h4>Play-by-play</h4>
      <div v-for="(grp, gi) in playGroups" :key="'g'+gi" class="pgroup">
        <div v-if="grp.label" class="phead mono">{{ grp.label }}</div>
        <div v-for="(p, i) in grp.plays" :key="gi+'-'+i" class="play" :class="{ score: p.scoring }">
          <span v-if="p.clock" class="clk mono">{{ p.clock }}</span>
          <span class="txt">{{ p.text }}</span>
        </div>
      </div>
    </div>

    <p v-if="!ls && !hasBox && !plays.length" class="empty">
      Live detail isn't available for this game.
    </p>
  </div>
</template>

<style scoped>
.card { border: 1px solid var(--line); border-radius: 12px; background: var(--panel); padding: 14px 16px; margin-bottom: 14px; }
.card h4 { font-size: 13px; margin-bottom: 10px; }
.linescore table, .ptable table { width: 100%; border-collapse: collapse; font-size: 13px; }
.linescore th, .linescore td { padding: 7px 10px; text-align: center; }
.linescore th.t, .linescore td.t { text-align: left; font-weight: 700; }
.linescore .tot { color: var(--accent); font-weight: 800; }
.linescore thead th { color: var(--muted); font-size: 11px; border-bottom: 1px solid var(--line); }
.tot-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(90px, 1fr)); gap: 8px; }
.tot-cell { display: flex; flex-direction: column; gap: 2px; padding: 8px 10px; background: var(--panel-2); border-radius: 8px; }
.tl { font-size: 10px; text-transform: uppercase; color: var(--muted); letter-spacing: 0.05em; }
.tv { font-weight: 700; }
.scroll { overflow-x: auto; }
.ptable th, .ptable td { padding: 6px 9px; text-align: right; white-space: nowrap; border-bottom: 1px solid rgba(53,42,96,0.5); font-size: 12px; }
.ptable th.nm, .ptable td.nm { text-align: left; }
.ptable thead th { color: var(--muted); font-size: 10px; text-transform: uppercase; }
.ptable h4 .sub { color: var(--muted); font-weight: 600; }
.pgroup { margin-top: 6px; }
.pgroup:first-of-type { margin-top: 0; }
.phead { color: var(--accent); font-size: 11px; text-transform: uppercase; letter-spacing: 0.06em;
  padding: 9px 0 5px; border-bottom: 1px solid var(--line); margin-bottom: 2px; }
.play { display: flex; gap: 12px; padding: 7px 0; border-bottom: 1px solid rgba(53,42,96,0.45); font-size: 13px; }
.play:last-child { border-bottom: none; }
.pgroup .play:last-child { border-bottom: none; }
.play.score { color: var(--accent); }
.clk { color: var(--muted); min-width: 56px; font-size: 11px; }
.txt { flex: 1; }
.empty { color: var(--muted); text-align: center; padding: 28px 16px; }
</style>
